import { Box, Text, Center, Spinner } from "@chakra-ui/react"
import { LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, Legend } from "recharts"
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@/components/ui/card"
import { ResponsiveContainer } from "recharts"
import { useTranslations } from "next-intl"
import moment from "moment"
import _ from "lodash"
import useKeywordDashboardStore from "@/store/KeywordDashboardStore"

function DashboardKeywordChart({ chart }) {
    const t = useTranslations("KeywordDashboard.Chart")
    const isLoading = useKeywordDashboardStore(state => state.isLoading)

    // Extract keyword groups (now using groupName instead of individual keyword)
    const keywordGroups = [...new Set(
        chart.flatMap(day =>
            day.values.map(value => value.keyword || value.groupName)
        ))
    ]

    // Transform data for the chart
    const chartData = chart.map((entry) => {
        const date = moment(entry.date).format("MMM DD")
        const dataPoint = { name: date }

        // Add count for each keyword group as a separate property
        entry.values.forEach((value) => {
            const groupKey = value.keyword || value.groupName
            dataPoint[groupKey] = value.count
        })

        return dataPoint
    })

    // Categorical series palette, validated on the white card surface: every hue
    // clears the lightness band, chroma floor, and colorblind separation between
    // neighbouring slots. Order is fixed — slot N always means the Nth series, so
    // adding or removing a keyword never repaints the ones that remain.
    const colors = [
        "#2a78d6", // blue
        "#eb6834", // orange
        "#1baf7a", // aqua
        "#eda100", // yellow
        "#e87ba4", // pink
        "#008300", // green
        "#4a3aa7", // violet
        "#e34948", // red
        "#0e8f9f", // teal
        "#c2185b", // raspberry
    ]

    const emptyView = (
        <Box height="340px">
            <Center height="100%">
                <Text as="b" fontSize="xl">
                    {t("emptyState")}
                </Text>
            </Center>
        </Box>
    )

    const loadingView = (
        <Box height="340px">
            <Center height="100%">
                <Spinner size="xl" color="main.red" thickness="4px" />
            </Center>
        </Box>
    )

    const chartComponent = (
        <Center marginTop="16px">
            <ResponsiveContainer minWidth={350} height={320}>
                <LineChart
                    data={chartData}
                    margin={{ top: 16, right: 36, bottom: 16, left: 20 }}>
                    {keywordGroups.map((keywordGroup, index) => (
                        <Line
                            key={keywordGroup}
                            type="monotone"
                            name={keywordGroup}
                            dataKey={keywordGroup}
                            stroke={colors[index % colors.length]}
                            strokeWidth={3}
                        />
                    ))}
                    <CartesianGrid opacity={0.5} vertical={false} />
                    <XAxis
                        dataKey="name"
                        axisLine={false}
                        tickLine={false}
                        interval="preserveStartEnd"
                        minTickGap={50}
                        angle={-45}
                        textAnchor="end"
                        tickMargin={15}
                        height={80}
                    />
                    <YAxis
                        axisLine={false}
                        tickLine={false}
                        width={60}
                        tickFormatter={(value) => value.toLocaleString()}
                    />
                    <Tooltip
                        formatter={(value) => value.toLocaleString()}
                    />
                    <Legend />
                </LineChart>
            </ResponsiveContainer>
        </Center>
    )

    return (
        <Card className="rounded-2xl border border-[#ced0d5] bg-white shadow-[0_2px_6px_rgba(0,0,0,0.06),0_1px_2px_rgba(0,0,0,0.05)]">
            <CardHeader>
                <CardTitle>{t("title")}</CardTitle>
                <CardDescription>{t("description")}</CardDescription>
            </CardHeader>
            <CardContent>
                {isLoading ? loadingView : chartData.length == 0 ? emptyView : chartComponent}
            </CardContent>
        </Card>
    )
}

export default DashboardKeywordChart